import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Typography } from '@/components/ui/typography';

import type { PostingResultScenario } from '../types';

interface ResultHeaderProps {
  scenario: PostingResultScenario;
  /** A posting batch row is still queued or posting for the first time. */
  isRunning?: boolean;
  /** The poster's name; posting batches only. */
  startedBy?: string | null;
}

const TITLE_COLOR: Record<PostingResultScenario, string> = {
  'all-success': 'text-fx-green',
  partial: 'text-fx-amber',
  'all-failed': 'text-fx-rose',
};

export function ResultHeader({
  scenario,
  isRunning = false,
  startedBy,
}: ResultHeaderProps) {
  if (isRunning) {
    return (
      <div className="flex w-full flex-col gap-3">
        <TitleRow startedBy={startedBy}>
          <Typography variant="h4">
            <FormattedMessage
              defaultMessage="Posting your jobs"
              description="Posting page title while job postings still run in the background"
            />
          </Typography>
        </TitleRow>
        <Typography variant="body1" className="text-fx-neutral-900">
          <FormattedMessage
            defaultMessage="You can leave this page and check progress from each opening's details page. We'll email you when it's done."
            description="Posting page note while job postings still run in the background"
          />
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <TitleRow startedBy={startedBy}>
        <Typography variant="h4" className={TITLE_COLOR[scenario]}>
          {scenario === 'all-success' && (
            <FormattedMessage
              defaultMessage="Your job has been posted successfully 🎉"
              description="Result page title when all job postings succeeded"
            />
          )}
          {scenario === 'partial' && (
            <FormattedMessage
              defaultMessage="Your job postings are complete with some issues"
              description="Result page title when some job postings failed"
            />
          )}
          {scenario === 'all-failed' && (
            <FormattedMessage
              defaultMessage="We couldn't post your jobs"
              description="Result page title when all job postings failed"
            />
          )}
        </Typography>
      </TitleRow>

      <Typography variant="body1" className="text-fx-neutral-900">
        {scenario === 'all-success' && (
          <FormattedMessage
            defaultMessage="All selected openings have been successfully posted across all channels. Applicants will begin flowing in soon."
            description="Result page description when all job postings succeeded"
          />
        )}
        {scenario === 'partial' && (
          <FormattedMessage
            defaultMessage="Some openings were successfully posted, while others encountered channel-level errors. Only successful postings were billed."
            description="Result page description when some job postings failed"
          />
        )}
        {scenario === 'all-failed' && (
          <FormattedMessage
            defaultMessage="All openings failed to post across the selected channels. No charges have been applied."
            description="Result page description when all job postings failed"
          />
        )}
      </Typography>
    </div>
  );
}

function TitleRow({
  startedBy,
  children,
}: {
  startedBy?: string | null;
  children: ReactNode;
}) {
  if (!startedBy) return <>{children}</>;

  return (
    <div className="flex items-start justify-between gap-4">
      {children}
      <Typography
        variant="small1"
        className="text-fx-neutral-500 shrink-0 whitespace-nowrap"
      >
        <FormattedMessage
          defaultMessage="Started by {name}"
          description="Posting page: who started the bulk job posting"
          values={{ name: startedBy }}
        />
      </Typography>
    </div>
  );
}
